import React from 'react';
import {
  HomeIcon,
  Layers,
  FileText,
  TestTube,
  Brain,
  Shield,
  Wrench,
  LogIn,
  KeyRound,
  Activity,
  BarChart3,
  GitBranch,
  User,
  ShoppingCart,
  Server,
  CheckSquare,
} from 'lucide-react';

// Eager imports — public/critical-path pages
import Index from './pages/Index.tsx';
import Auth from './pages/Auth.tsx';
import AuthCallback from './pages/AuthCallback.tsx';
import PasswordReset from './pages/PasswordReset.tsx';

// Lazy imports — authenticated/heavy pages
const Canvas = React.lazy(() => import('./pages/Canvas.tsx'));
const CloudFoundationDashboard = React.lazy(() => import('./pages/CloudFoundationDashboard.tsx'));
const DependencyPlanning = React.lazy(() => import('./pages/DependencyPlanning.tsx'));
const Documentation = React.lazy(() => import('./pages/Documentation.tsx'));
const TestingDashboard = React.lazy(() => import('./pages/TestingDashboard.tsx'));
const AdvancedFeatures = React.lazy(() => import('./pages/AdvancedFeatures.tsx'));
const EnterpriseManagement = React.lazy(() => import('./pages/EnterpriseManagement.tsx'));
const IDPToolsCatalog = React.lazy(() => import('./pages/IDPToolsCatalog.tsx'));
const IDPTemplates = React.lazy(() => import('./pages/IDPTemplates.tsx'));
const SelfHealingDashboard = React.lazy(() => import('./pages/SelfHealingDashboard.tsx'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage.tsx'));
const MarketplaceBrowse = React.lazy(() =>
  import('./pages/MarketplaceBrowse.tsx').then(m => ({ default: m.MarketplaceBrowse }))
);
const ValidationEngine = React.lazy(() => import('./pages/ValidationEngine.tsx'));
const VClusterPage = React.lazy(() => import('./pages/VClusterPage.tsx'));
const PlatformLandscape = React.lazy(() => import('./components/PlatformLandscape'));
const MetricsDashboard = React.lazy(() => import('./pages/MetricsDashboard.tsx'));
const DocsHub = React.lazy(() => import('./pages/DocsHub.tsx'));
const SSOConfigPage = React.lazy(() => import('./pages/SSOConfigPage.tsx'));
const TermsOfServicePage = React.lazy(() => import('./pages/legal/TermsOfServicePage.tsx'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/legal/PrivacyPolicyPage.tsx'));
const CookiePolicyPage = React.lazy(() => import('./pages/legal/CookiePolicyPage.tsx'));
const CompliancePage = React.lazy(() => import('./pages/legal/CompliancePage.tsx'));
const DataSovereigntyManifestoPage = React.lazy(
  () => import('./pages/legal/DataSovereigntyManifestoPage.tsx')
);
const PrivacyCenterPage = React.lazy(() => import('./pages/legal/PrivacyCenterPage.tsx'));
const SecurityAndDisclosurePage = React.lazy(
  () => import('./pages/legal/SecurityAndDisclosurePage.tsx')
);

/**
 * Route access levels for navigation and security
 * - 'public': Accessible to all users (unauthenticated)
 * - 'trial': Accessible to trial users
 * - 'authenticated': Requires authentication
 * - 'admin': Requires admin privileges
 */
type RouteAccess = 'public' | 'trial' | 'authenticated' | 'admin';

interface NavItem {
  title: string;
  to: string;
  icon: React.ReactNode;
  page: React.ReactNode;
  access: RouteAccess;
}

/**
 * Central place for defining the navigation items. Used for navigation components and routing.
 */
export const navItems: NavItem[] = [
  {
    title: 'Platform Home',
    to: '/',
    icon: <HomeIcon className="h-4 w-4" />,
    page: <Index />,
    access: 'public',
  },
  {
    title: 'Design Canvas',
    to: '/canvas',
    icon: <Layers className="h-4 w-4" />,
    page: <Canvas />,
    access: 'authenticated',
  },
  {
    title: 'Cloud Foundation',
    to: '/cloud-foundation',
    icon: <BarChart3 className="h-4 w-4" />,
    page: <CloudFoundationDashboard />,
    access: 'authenticated',
  },
  {
    title: 'Dependency Planning',
    to: '/dependency-planning',
    icon: <GitBranch className="h-4 w-4" />,
    page: <DependencyPlanning />,
    access: 'authenticated',
  },
  {
    title: 'Documentation',
    to: '/documentation',
    icon: <FileText className="h-4 w-4" />,
    page: <Documentation />,
    access: 'public',
  },
  {
    title: 'Testing Dashboard',
    to: '/testing-dashboard',
    icon: <TestTube className="h-4 w-4" />,
    page: <TestingDashboard />,
    access: 'authenticated',
  },
  {
    title: 'AI & Innovation',
    to: '/advanced-features',
    icon: <Brain className="h-4 w-4" />,
    page: <AdvancedFeatures />,
    access: 'authenticated',
  },
  {
    title: 'Enterprise Management',
    to: '/enterprise-management',
    icon: <Shield className="h-4 w-4" />,
    page: <EnterpriseManagement />,
    access: 'admin',
  },
  {
    title: 'Business Metrics',
    to: '/admin/metrics',
    icon: <BarChart3 className="h-4 w-4" />,
    page: <MetricsDashboard />,
    access: 'admin',
  },
  {
    title: 'SSO Configuration',
    to: '/admin/sso',
    icon: <Shield className="h-4 w-4" />,
    page: <SSOConfigPage />,
    access: 'admin',
  },
  {
    title: 'IDP Tools Catalog',
    to: '/idp-tools-catalog',
    icon: <Wrench className="h-4 w-4" />,
    page: <IDPToolsCatalog />,
    access: 'authenticated',
  },
  {
    title: 'IDP Templates',
    to: '/idp-templates',
    icon: <FileText className="h-4 w-4" />,
    page: <IDPTemplates />,
    access: 'authenticated',
  },
  {
    title: 'Platform Landscape',
    to: '/platform-landscape',
    icon: <Server className="h-4 w-4" />,
    page: <PlatformLandscape />,
    access: 'public',
  },
  {
    title: 'Self-Healing Dashboard',
    to: '/self-healing',
    icon: <Activity className="h-4 w-4" />,
    page: <SelfHealingDashboard />,
    access: 'authenticated',
  },
  {
    title: 'Marketplace',
    to: '/marketplace',
    icon: <ShoppingCart className="h-4 w-4" />,
    page: <MarketplaceBrowse />,
    access: 'authenticated',
  },
  {
    title: 'Virtual Clusters',
    to: '/vclusters',
    icon: <Server className="h-4 w-4" />,
    page: <VClusterPage />,
    access: 'authenticated',
  },
  {
    title: 'Validation',
    to: '/validation',
    icon: <CheckSquare className="h-4 w-4" />,
    page: <ValidationEngine />,
    access: 'authenticated',
  },
  {
    title: 'Docs & Resources',
    to: '/docs',
    icon: <FileText className="h-4 w-4" />,
    page: <DocsHub />,
    access: 'public',
  },
  {
    title: 'Terms of Service',
    to: '/legal/terms-of-service',
    icon: <FileText className="h-4 w-4" />,
    page: <TermsOfServicePage />,
    access: 'public',
  },
  {
    title: 'Privacy Policy',
    to: '/legal/privacy-policy',
    icon: <FileText className="h-4 w-4" />,
    page: <PrivacyPolicyPage />,
    access: 'public',
  },
  {
    title: 'Cookie Policy',
    to: '/legal/cookie-policy',
    icon: <FileText className="h-4 w-4" />,
    page: <CookiePolicyPage />,
    access: 'public',
  },
  {
    title: 'Compliance',
    to: '/legal/compliance',
    icon: <FileText className="h-4 w-4" />,
    page: <CompliancePage />,
    access: 'public',
  },
  {
    title: 'Data Sovereignty',
    to: '/legal/data-sovereignty',
    icon: <FileText className="h-4 w-4" />,
    page: <DataSovereigntyManifestoPage />,
    access: 'public',
  },
  {
    title: 'Privacy Center',
    to: '/privacy-center',
    icon: <FileText className="h-4 w-4" />,
    page: <PrivacyCenterPage />,
    access: 'public',
  },
  {
    title: 'Security & Disclosure',
    to: '/legal/security',
    icon: <FileText className="h-4 w-4" />,
    page: <SecurityAndDisclosurePage />,
    access: 'public',
  },
  {
    title: 'Authentication',
    to: '/auth',
    icon: <LogIn className="h-4 w-4" />,
    page: <Auth />,
    access: 'public',
  },
  {
    title: 'Auth Callback',
    to: '/auth/callback',
    icon: <LogIn className="h-4 w-4" />,
    page: <AuthCallback />,
    access: 'public',
  },
  {
    title: 'Password Reset',
    to: '/auth/reset',
    icon: <KeyRound className="h-4 w-4" />,
    page: <PasswordReset />,
    access: 'public',
  },
  {
    title: 'Profile',
    to: '/profile',
    icon: <User className="h-4 w-4" />,
    page: <ProfilePage />,
    access: 'authenticated',
  },
];

export type { NavItem, RouteAccess };
